// User management — admins and sales reps. Admin-only screen.
// Data source: public.users in Supabase (see src/api.js).
import React from 'react';
import { Icon, Pill, Button, Card, Select } from './ui.jsx';
import { api } from './api.js';
import { hasPermission } from './auth.jsx';

// Async — list all users in the current tenant (RLS enforces scoping).
export async function loadUsers() {
  return api.users.list();
}

// Mirror rep list into window.REPS so existing screens (leaderboard,
// assignments, drawer) see live data. Called after every CRUD.
export function mirrorRepsToWindow(users) {
  const reps = users
    .filter(u => u.role === 'rep' && u.active)
    .map(u => ({
      id: u.repId || u.id, name: u.name, email: u.email,
      start: u.createdAt, active: !!u.active,
    }));
  window.REPS = reps;
}

// Generate a short, human-readable rep id. Collisions are rare but the DB
// has a unique constraint so inserts will fail loudly if one happens.
export function genRepId() {
  return 'r-' + Math.random().toString(36).slice(2, 8);
}

// ================ S22 · Users list ================
export function ScreenUsers({ users, onAdd, onEdit, onToggleActive, onArchive, onUnarchive, onNav, currentUser }) {
  const [modal, setModal]     = React.useState(null); // null | 'add' | editingUser
  const [filter, setFilter]   = React.useState('all');
  // Permission gates — Approver gets read-only access here. Add / edit /
  // archive / send-invitation require explicit permissions on top of the
  // 'users.read' Approver already has.
  const canInvite = hasPermission(currentUser, 'users', 'invite');
  const canEditUsers = hasPermission(currentUser, 'users', 'edit');
  // Confirm dialog state for archive (since it's a recoverable but
  // intent-revealing action — show a destructive-styled confirm).
  const [archiveTarget, setArchiveTarget] = React.useState(null);
  // Shown when a staged user is promoted — same temp-password screen as
  // a fresh invite.
  const [inviteResult, setInviteResult] = React.useState(null);
  const [sendingId, setSendingId]       = React.useState(null);

  // 'all' / 'admin' / 'rep' / 'active' all hide archived users by default.
  // Only the explicit 'archived' filter surfaces them.
  const filtered = users.filter(u => {
    if (filter === 'archived') return u.archived;
    if (u.archived) return false;
    if (filter === 'all')    return true;
    if (filter === 'active') return u.active;
    if (filter === 'rep')    return u.role === 'rep';
    if (filter === 'admin')  return u.role === 'admin';
    return true;
  });

  const archivedCount = users.filter(u => u.archived).length;
  const deactivatedCount = users.filter(u => !u.active && !u.archived).length;

  const doArchive = async () => {
    if (!archiveTarget) return;
    await onArchive(archiveTarget.id);
    setArchiveTarget(null);
  };

  const sendInvitation = async (u) => {
    setSendingId(u.id);
    try {
      const { api } = await import('./api.js');
      const result = await api.users.sendInvitation(u);
      setInviteResult(result);
      // The parent owns the users state; trigger a refresh by toggling
      // active (no-op semantically) — simplest reload signal. The
      // existing onEdit handler will re-fetch via update.
      onEdit(u.id, { active: u.active });
    } catch (err) {
      alert('Could not send invitation: ' + (err.message || err));
    } finally { setSendingId(null); }
  };

  // For 'View as' — push the rep's dashboard path with ?as=<id>. The rep
  // dashboard reads the param and loads that rep's invoices in preview mode.
  const viewAs = (u) => {
    const path = `/rep/this-month?as=${u.id}`;
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">Users &amp; permissions</h1>
          <div className="text-[13px] text-neutral-500 mt-1">
            {users.filter(u => u.role === 'admin' && !u.archived).length} admin{users.filter(u => u.role === 'admin' && !u.archived).length === 1 ? '' : 's'} ·
            {' '}{users.filter(u => u.role === 'rep' && !u.archived).length} sales rep{users.filter(u => u.role === 'rep' && !u.archived).length === 1 ? '' : 's'} ·
            {' '}{deactivatedCount} deactivated
            {archivedCount > 0 && (
              <> · <button onClick={() => setFilter('archived')} className="text-primary-700 hover:text-primary-900 font-medium">{archivedCount} archived</button></>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select size="md" value={filter} onChange={setFilter} options={[
            {value:'all',     label:'All users'},
            {value:'admin',   label:'Admins'},
            {value:'rep',     label:'Sales reps'},
            {value:'active',  label:'Active only'},
            {value:'archived',label:`Archived${archivedCount > 0 ? ` (${archivedCount})` : ''}`},
          ]}/>
          {canInvite && <Button variant="primary" icon="plus" onClick={() => setModal('add')}>Add user</Button>}
        </div>
      </div>

      <Card padding="p-0">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">
              <th className="px-4 py-2.5 font-semibold">Name</th>
              <th className="px-4 py-2.5 font-semibold">Email</th>
              <th className="px-4 py-2.5 font-semibold">Role</th>
              <th className="px-4 py-2.5 font-semibold">Start date</th>
              <th className="px-4 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 w-32 text-right"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-[13px] text-neutral-500">No users match this filter.</td></tr>
            )}
            {filtered.map(u => (
              <tr key={u.id} className={`hover:bg-primary-100/30 ${u.archived ? 'opacity-50 bg-neutral-50/50' : !u.active ? 'opacity-60' : ''}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className={`w-7 h-7 rounded-full ${u.role === 'admin' ? 'bg-primary-900 text-white' : 'bg-neutral-100 text-neutral-700'} flex items-center justify-center text-[10px] font-bold`}>
                      {u.name.split(' ').map(s=>s[0]).join('')}
                    </div>
                    <div>
                      <div className="font-semibold text-neutral-900">{u.name}</div>
                      <div className="text-[11px] text-neutral-500 font-mono">{u.id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-neutral-700">{u.email}</td>
                <td className="px-4 py-3">
                  <Pill tone={u.role === 'admin' ? 'brand' : 'y2'}>{u.role === 'admin' ? 'Admin' : 'Sales Rep'}</Pill>
                </td>
                <td className="px-4 py-3 text-neutral-700 font-mono">{u.start || u.createdAt || '—'}</td>
                <td className="px-4 py-3">
                  {u.archived ? <Pill tone="neutral">Archived</Pill>
                    : u.staged ? <Pill tone="info">Staged — not invited</Pill>
                    : u.active ? <Pill tone="paid">Active</Pill>
                    : <Pill tone="warn">Inactive</Pill>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {u.archived ? (
                    canEditUsers ? (
                      <button onClick={() => onUnarchive(u.id)} className="text-[12px] text-primary-700 hover:text-primary-900 font-medium">
                        Restore
                      </button>
                    ) : <span className="text-[12px] text-neutral-400">read-only</span>
                  ) : (
                    <>
                      {canEditUsers && (
                        <button onClick={() => setModal(u)} className="text-[12px] text-primary-700 hover:text-primary-900 font-medium mr-3">Edit</button>
                      )}
                      {u.staged ? (
                        canInvite && (
                          <button onClick={() => sendInvitation(u)} disabled={sendingId === u.id} className="text-[12px] text-emerald-700 hover:text-emerald-900 font-medium mr-3 disabled:opacity-50">
                            {sendingId === u.id ? 'Sending…' : 'Send invitation'}
                          </button>
                        )
                      ) : (
                        canEditUsers && (
                          <button onClick={() => onToggleActive(u.id)} className="text-[12px] text-neutral-600 hover:text-neutral-900 font-medium mr-3">
                            {u.active ? 'Deactivate' : 'Reactivate'}
                          </button>
                        )
                      )}
                      {u.role === 'rep' && !u.archived && (
                        <button onClick={() => viewAs(u)} className="text-[12px] text-neutral-600 hover:text-neutral-900 font-medium mr-3">
                          View as
                        </button>
                      )}
                      {!u.active && !u.staged && canEditUsers && (
                        <button onClick={() => setArchiveTarget(u)} className="text-[12px] text-red-700 hover:text-red-900 font-medium">
                          Archive
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {modal && (
        <UserModal
          initial={modal === 'add' ? null : modal}
          onCancel={() => setModal(null)}
          onInvite={onAdd}
          onEdit={(data) => { onEdit(modal.id, data); setModal(null); }}
        />
      )}

      {inviteResult && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center p-6" onClick={() => setInviteResult(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-[480px] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center"><Icon name="check" size={14}/></div>
                <div className="text-[16px] font-bold text-neutral-900">Invitation ready</div>
              </div>
              <button onClick={() => setInviteResult(null)} className="text-neutral-400 hover:text-neutral-700"><Icon name="close" size={14}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-[13px] text-neutral-700">
                <b>{inviteResult.user.name}</b> ({inviteResult.user.email}) can now sign in.
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-amber-800 mb-1.5">Temporary password — shown once</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-[13px] bg-white border border-amber-200 rounded px-2.5 py-1.5 text-neutral-900 select-all">
                    {inviteResult.tempPassword}
                  </code>
                  <Button size="sm" variant="secondary" onClick={() => navigator.clipboard.writeText(inviteResult.tempPassword)}>Copy</Button>
                </div>
                <div className="text-[11px] text-amber-800 mt-2 leading-relaxed">
                  Share this through your channel of choice (Slack, DM). We don't email it. The user changes it via "Forgot password" after first sign-in.
                </div>
              </div>
            </div>
            <div className="px-5 py-3 bg-canvas/60 border-t border-neutral-200 flex items-center justify-end gap-2">
              <Button variant="primary" size="sm" onClick={() => setInviteResult(null)}>Done</Button>
            </div>
          </div>
        </div>
      )}

      {archiveTarget && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center p-6" onClick={() => setArchiveTarget(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-[460px] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-neutral-200">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-red-50 border border-red-200 text-red-700 flex items-center justify-center"><Icon name="warn" size={14}/></div>
                <div className="text-[16px] font-bold text-neutral-900">Archive {archiveTarget.name}?</div>
              </div>
            </div>
            <div className="p-5 space-y-3 text-[13px] text-neutral-700 leading-relaxed">
              <p>
                Archiving moves <b>{archiveTarget.name}</b> out of the working roster. They'll be hidden
                from the default user list and the rep dropdowns, but their row stays in the database
                so historical runs, approvals, and audit entries that reference them still work.
              </p>
              <p>
                You can restore them later — restoring puts them back into the deactivated state, not
                automatically active.
              </p>
            </div>
            <div className="px-5 py-3 bg-canvas/60 border-t border-neutral-200 flex items-center justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setArchiveTarget(null)}>Cancel</Button>
              <Button variant="destructive" size="sm" icon="lock" onClick={doArchive}>Archive user</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UserModal({ initial, onCancel, onInvite, onEdit }) {
  const isEditing = !!initial;
  const [name, setName]   = React.useState(initial?.name || '');
  const [email, setEmail] = React.useState(initial?.email || '');
  const [role, setRole]   = React.useState(initial?.role || 'rep');
  // Rates as % strings for the input. Empty = inherit tenant default.
  const [y1Rate, setY1Rate] = React.useState(initial?.y1Rate != null ? (initial.y1Rate * 100).toString() : '');
  const [y2Rate, setY2Rate] = React.useState(initial?.y2Rate != null ? (initial.y2Rate * 100).toString() : '');
  const [commissionPlanId, setCommissionPlanId] = React.useState(initial?.commissionPlanId || '');
  // null/empty = inherit plan; otherwise force 'accrual' or 'cash'.
  const [paymentBasis, setPaymentBasis] = React.useState(initial?.paymentBasis || '');
  const [plans, setPlans]   = React.useState([]);
  const [roleId, setRoleId] = React.useState(initial?.roleId || '');
  const [roles, setRoles]   = React.useState([]);
  const [error, setError] = React.useState(null);
  const [busy, setBusy]   = React.useState(false);
  const [invited, setInvited] = React.useState(null);
  // Staging mode — when on, create the public.users row but don't invite yet.
  // Default: ON for new users so admins can set everything up before
  // notifying reps. Switch off to invite immediately and get a temp password.
  const [stageOnly, setStageOnly] = React.useState(true);
  const [staged, setStaged] = React.useState(null);

  // Load plans + roles for the dropdowns.
  React.useEffect(() => {
    let alive = true;
    import('./api.js').then(({ api }) => Promise.all([api.commissionPlans.list(), api.roles.list()]))
      .then(([planList, roleList]) => {
        if (!alive) return;
        setPlans(planList); setRoles(roleList);
        // For new users: default to the system "Sales Rep" role to match the
        // previous behavior. For edits: honor whatever is on the row.
        if (!initial?.roleId && !roleId) {
          const defaultRole = roleList.find(r => r.isSystemRole && r.baseRole === 'rep');
          if (defaultRole) {
            setRoleId(defaultRole.id);
            setRole(defaultRole.baseRole);
          }
        }
      })
      .catch(err => console.warn('plans/roles load:', err));
    return () => { alive = false; };
  }, []);

  // Whenever the selected role changes, mirror its base into the legacy
  // `role` text field so RLS still works.
  React.useEffect(() => {
    if (!roleId) return;
    const r = roles.find(x => x.id === roleId);
    if (r) setRole(r.baseRole);
  }, [roleId, roles]);

  // Validate & convert percent strings back to fractions.
  const parseRate = (s) => {
    if (s === '' || s == null) return null; // inherit default
    const n = Number(s);
    if (!isFinite(n) || n < 0 || n > 100) return '__INVALID__';
    return n / 100;
  };

  const submit = async () => {
    setError(null);
    if (!name.trim()) return setError('Name is required.');
    if (!email.trim() || !/@/.test(email)) return setError('A valid email is required.');
    const y1 = parseRate(y1Rate);
    const y2 = parseRate(y2Rate);
    if (y1 === '__INVALID__' || y2 === '__INVALID__') return setError('Rates must be numbers between 0 and 100.');
    const ratePayload = role === 'rep'
      ? { y1Rate: y1, y2Rate: y2, commissionPlanId: commissionPlanId || null, paymentBasis: paymentBasis || null }
      : { y1Rate: null, y2Rate: null, commissionPlanId: null, paymentBasis: null };
    const rolePayload = { role, roleId: roleId || null };
    if (isEditing) {
      onEdit({ name: name.trim(), email: email.trim(), active: true, ...rolePayload, ...ratePayload });
      return;
    }
    setBusy(true);
    try {
      if (stageOnly) {
        // Stage path — no edge function call, no auth user, no temp password.
        const { api } = await import('./api.js');
        const stagedUser = await api.users.stage({
          email: email.trim(), name: name.trim(),
          ...rolePayload, ...ratePayload,
        });
        setStaged(stagedUser);
      } else {
        const result = await onInvite({ name: name.trim(), email: email.trim(), ...rolePayload, ...ratePayload });
        setInvited(result);
      }
    } catch (err) {
      setError(err.message || 'Could not create user.');
    } finally {
      setBusy(false);
    }
  };

  // ---------- Success screen after staging (no temp password yet) ----------
  if (staged) {
    return (
      <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center p-6" onClick={onCancel}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-[480px] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-blue-50 border border-blue-200 text-blue-700 flex items-center justify-center"><Icon name="lock" size={14}/></div>
              <div className="text-[16px] font-bold text-neutral-900">User staged</div>
            </div>
            <button onClick={onCancel} className="text-neutral-400 hover:text-neutral-700"><Icon name="close" size={14}/></button>
          </div>
          <div className="p-5 space-y-3 text-[13px] text-neutral-700">
            <p><b>{staged.name}</b> ({staged.email}) has been added to the system as a <b>{staged.role === 'admin' ? 'Admin' : 'Sales Rep'}</b>. They <b>cannot sign in yet</b> — no login has been created.</p>
            <p>You can now:</p>
            <ul className="list-disc pl-5 space-y-1 text-[12.5px] text-neutral-600">
              <li>Assign customers to them in the Customers screen</li>
              <li>Configure their commission plan and per-rep rates</li>
              <li>Preview what their dashboard will look like (Users list → <b>View as</b>)</li>
              <li>Click <b>Send invitation</b> on their row when you're ready — that creates the login and shows you a temp password to share</li>
            </ul>
          </div>
          <div className="px-5 py-3 bg-canvas/60 border-t border-neutral-200 flex items-center justify-end gap-2">
            <Button variant="primary" size="sm" onClick={onCancel}>Done</Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Success screen after invite ----------
  if (invited) {
    return (
      <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center p-6" onClick={onCancel}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-[480px] overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center"><Icon name="check" size={14}/></div>
              <div className="text-[16px] font-bold text-neutral-900">User created</div>
            </div>
            <button onClick={onCancel} className="text-neutral-400 hover:text-neutral-700"><Icon name="close" size={14}/></button>
          </div>
          <div className="p-5 space-y-4">
            <div className="text-[13px] text-neutral-700">
              <b>{invited.user.name}</b> ({invited.user.email}) was added as a <b>{invited.user.role === 'admin' ? 'Admin' : 'Sales Rep'}</b>.
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-amber-800 mb-1.5">
                Temporary password — shown once
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-[13px] bg-white border border-amber-200 rounded px-2.5 py-1.5 text-neutral-900 select-all">
                  {invited.tempPassword}
                </code>
                <Button size="sm" variant="secondary" onClick={() => navigator.clipboard.writeText(invited.tempPassword)}>
                  Copy
                </Button>
              </div>
              <div className="text-[11px] text-amber-800 mt-2 leading-relaxed">
                Share this with <b>{invited.user.email}</b> through your preferred channel (Slack, DM, etc.) —
                we don't email it automatically. They'll use it for their first sign-in, then change it via
                "Forgot password".
              </div>
            </div>

            <div className="text-[11px] text-neutral-500 leading-relaxed border-t border-neutral-100 pt-3">
              {invited.message}
            </div>
          </div>
          <div className="px-5 py-3 bg-canvas/60 border-t border-neutral-200 flex items-center justify-end gap-2">
            <Button variant="primary" size="sm" onClick={onCancel}>Done</Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Form ----------
  return (
    <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center p-6" onClick={onCancel}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[480px] overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div className="text-[16px] font-bold text-neutral-900">{isEditing ? 'Edit user' : 'Invite a new user'}</div>
          <button onClick={onCancel} className="text-neutral-400 hover:text-neutral-700"><Icon name="close" size={14}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Role</label>
            <select
              value={roleId}
              onChange={e => setRoleId(e.target.value)}
              disabled={busy || roles.length === 0}
              className="w-full h-10 px-3 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50">
              <option value="" disabled>Select a role…</option>
              {roles.map(r => (
                <option key={r.id} value={r.id}>
                  {r.name}{r.isSystemRole ? ' (system)' : ''}
                </option>
              ))}
            </select>
            {(() => {
              const sel = roles.find(r => r.id === roleId);
              return sel?.description ? (
                <div className="text-[11.5px] text-neutral-500 mt-1.5 leading-relaxed">{sel.description}</div>
              ) : null;
            })()}
            <div className="text-[11px] text-neutral-400 mt-1.5">
              Need a role that doesn't exist? Create one under <b>Users → Roles &amp; permissions</b>.
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Full name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Jane Doe" disabled={busy}
              className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"/>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="name@company.com" disabled={busy || isEditing}
              className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50"/>
            {isEditing && <div className="text-[11px] text-neutral-500 mt-1">Email can't be changed after the user is created.</div>}
          </div>

          {role === 'rep' && (
            <div className="border-t border-neutral-100 pt-4">
              <div className="text-[12px] font-semibold text-neutral-700 mb-1">Commission plan</div>
              <div className="text-[11px] text-neutral-500 mb-3 leading-relaxed">
                Pick the plan that defines this rep's tier ladder and deductions. <b>Default</b> falls back to the tenant's default plan.
              </div>
              <select value={commissionPlanId} onChange={e => setCommissionPlanId(e.target.value)}
                className="w-full h-10 px-3 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 mb-4">
                <option value="">— Tenant default plan —</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>{p.name}{p.isDefault ? ' (default)' : ''}</option>
                ))}
              </select>

              {/* Per-rep payment timing override.
                  Empty value = inherit the plan's setting. */}
              <div className="text-[12px] font-semibold text-neutral-700 mb-1 mt-4">When does this rep earn commission?</div>
              <div className="text-[11px] text-neutral-500 mb-3 leading-relaxed">
                <b>Inherit</b> follows the plan's setting; pick an explicit value to override for this rep only.
                {(() => {
                  const sel = plans.find(p => p.id === commissionPlanId);
                  const planBasis = sel?.paymentBasis || (plans.find(p => p.isDefault)?.paymentBasis) || 'accrual';
                  return paymentBasis === ''
                    ? <> Plan default: <b>{planBasis === 'cash' ? 'Hold until customer pays' : 'Pay on every invoice'}</b>.</>
                    : null;
                })()}
              </div>
              <select value={paymentBasis} onChange={e => setPaymentBasis(e.target.value)}
                className="w-full h-10 px-3 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 mb-4">
                <option value="">— Inherit from plan —</option>
                <option value="accrual">Pay on every invoice (immediate)</option>
                <option value="cash">Hold until customer pays (carry over once collected)</option>
              </select>

              <div className="text-[12px] font-semibold text-neutral-700 mb-1 mt-4">Per-rep rate overrides</div>
              <div className="text-[11px] text-neutral-500 mb-3 leading-relaxed">
                Override the plan's Y1 / Y2+ rates for this rep specifically. Leave blank to inherit the plan's rate.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-neutral-600 mb-1.5">Y1 rate (new client)</label>
                  <div className="relative">
                    <input type="number" step="0.01" min="0" max="100" value={y1Rate} onChange={e => setY1Rate(e.target.value)} placeholder="default"
                      className="w-full h-9 pl-3 pr-8 text-[13px] font-mono rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 font-mono">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-neutral-600 mb-1.5">Y2+ rate (returning)</label>
                  <div className="relative">
                    <input type="number" step="0.01" min="0" max="100" value={y2Rate} onChange={e => setY2Rate(e.target.value)} placeholder="default"
                      className="w-full h-9 pl-3 pr-8 text-[13px] font-mono rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700"/>
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[12px] text-neutral-400 font-mono">%</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}

          {!isEditing && (
            <div className="border-t border-neutral-100 pt-4">
              <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${stageOnly ? 'border-primary-300 bg-primary-100/30' : 'border-neutral-200 hover:border-neutral-300'}`}>
                <input type="checkbox" checked={stageOnly} onChange={e => setStageOnly(e.target.checked)} disabled={busy}
                  className="mt-1 w-4 h-4 accent-primary-700"/>
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-neutral-900">Stage only — don't enable login yet</div>
                  <div className="text-[11.5px] text-neutral-600 mt-0.5 leading-relaxed">
                    Add this user to the system so you can assign customers and configure their plan, but skip creating a login.
                    Click <b>Send invitation</b> on their row later when you're ready to give them access.
                  </div>
                </div>
              </label>
              {!stageOnly && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2 leading-relaxed">
                  <b>Heads up:</b> turning this off creates the login immediately and shows you a temp password to share.
                  We don't email anything — but you'll be one Slack message away from notifying them.
                </div>
              )}
            </div>
          )}

          <div className="text-[11px] text-neutral-500 leading-relaxed border-t border-neutral-100 pt-3">
            {isEditing
              ? 'Changes take effect immediately.'
              : stageOnly
                ? 'Staged users have a record in the system but can\'t sign in. You can preview their dashboard before inviting them.'
                : 'We\'ll create the account and give you a temporary password to share with them. They can change it via "Forgot password" after their first sign-in.'}
          </div>
        </div>
        <div className="px-5 py-3 bg-canvas/60 border-t border-neutral-200 flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="primary" size="sm" icon={busy ? null : 'check'} onClick={submit} disabled={busy}>
            {busy ? (stageOnly ? 'Staging…' : 'Creating…') : isEditing ? 'Save changes' : stageOnly ? 'Stage user' : 'Create &amp; invite'}
          </Button>
        </div>
      </div>
    </div>
  );
}

